import React, { useState } from 'react';
import { Search, BookOpen, Clock, CheckCircle2, Play, Award, Sparkles, Eye, ShieldAlert, Calendar, CreditCard } from 'lucide-react';
import { COURSE_PERIODS, COURSE_CATEGORIES } from '../../data/coursesData';
import { localCourseResumen } from '../../utils/courseStructure';
import '../../styles/CursosVortex.css';

export const CourseCatalog = ({
  courses = [],
  isSubscribed = false,
  isAuthenticated = false,
  onSelectCourse,
  onOpenDetailModal,
  onEnroll,
  onOpenAuth,
  onOpenLector
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState('p1');
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredCourses = courses.filter((course) => {
    const matchesCategory =
      selectedCategory === 'Todos' || course.category === selectedCategory;
    const matchesPeriod =
      !selectedPeriod || course.periodId === selectedPeriod || selectedPeriod === 'all';
    const matchesSearch =
      !searchQuery.trim() ||
      course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.category.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesPeriod && matchesSearch;
  });

  return (
    <div className="vx-cursos-container">

      {/* ── Hero Header ── */}
      <div className="vx-hero">
        <div>
          <div className="vx-hero-badge">
            <Sparkles className="w-4 h-4 text-[#c5a059]" />
            <span>Capacitación Continua para Ascensos SEMAR 2026</span>
          </div>
          <h1>
            Plataforma de <span className="vx-hero-highlight">Cursos Vortex</span>
          </h1>
          <p className="vx-hero-desc">
            Preparación teórica especializada con clases en video, quices evaluativos, materiales en PDF y evaluaciones
            organizadas en 3 periodos anuales para el personal de la Armada de México.
          </p>
        </div>

        <div className="vx-hero-actions">
          {!isAuthenticated ? (
            <button className="btn-vx-action" onClick={onOpenAuth}>
              🔐 Iniciar Sesión / Registrarse
            </button>
          ) : isSubscribed ? (
            <div className="vx-badge-subscribers">
              <CheckCircle2 className="w-4 h-4" />
              <span>Licencia Cursos Activa</span>
            </div>
          ) : (
            <button
              className="btn-vx-action"
              onClick={() => { const first = courses[0]; if (first) onEnroll(first); }}
            >
              <CreditCard className="w-4 h-4" />
              <span>Adquirir acceso al periodo — $300 MXN</span>
            </button>
          )}
          {isAuthenticated && isSubscribed && onOpenLector && (
            <button className="btn-vx-action" onClick={onOpenLector}>
              <BookOpen className="w-4 h-4" />
              <span>📖 Lector de Temas</span>
            </button>
          )}
        </div>
      </div>

      {/* ── Periodos Anuales ── */}
      <div>
        <div className="vx-section-header">
          <h2 className="vx-section-title">
            <Calendar className="w-5 h-5" />
            <span>Periodos de Estudio en el Año</span>
          </h2>
          <span className="vx-section-badge">3 PERIODOS ANUALES</span>
        </div>

        <div className="vx-periods-grid">
          {COURSE_PERIODS.map((period) => {
            const isSelected = selectedPeriod === period.id;
            return (
              <div
                key={period.id}
                onClick={() => setSelectedPeriod(period.id)}
                className={`vx-period-card${isSelected ? ' active' : ''}`}
              >
                <div>
                  <div className="vx-period-card-top">
                    <span className="vx-period-short">{period.shortName}</span>
                    <span className={`vx-period-pill${period.active ? ' is-active' : ''}`}>
                      {period.badge}
                    </span>
                  </div>
                  <h3>{period.name}</h3>
                  <p>{period.description}</p>
                </div>
                <div className="vx-period-footer">
                  <span>Fechas: {period.months}</span>
                  <span className={isSelected ? 'selected' : ''}>
                    {isSelected ? '✓ Seleccionado' : 'Seleccionar'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Alerta de Exámenes */}
        <div style={{ marginTop: '1rem' }}>
          <div className="vx-alert-banner">
            <div className="vx-alert-icon">
              <ShieldAlert className="w-5 h-5" />
            </div>
            <div>
              <h4>Periodo de Exámenes de Promoción SEMAR (Junio, Julio y Agosto)</h4>
              <p>
                Durante los meses de Junio a Agosto las evaluaciones se ejecutan directamente en la{' '}
                <strong style={{ color: '#fff' }}>App Móvil AscensosApp Vortex</strong> con simuladores intensivos y banco de preguntas actualizado.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Filtros y Buscador ── */}
      <div className="vx-filters-bar">
        <div className="vx-cat-list">
          {COURSE_CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`btn-vx-cat${selectedCategory === cat ? ' active' : ''}`}
            >
              <span>
                {cat === 'Todos' && '✨'}
                {cat === 'Conocimientos Marineros' && '⚓'}
                {cat === 'Legislación Naval' && '⚖️'}
                {cat === 'Conocimientos Específicos' && '🎯'}
              </span>
              <span>{cat}</span>
            </button>
          ))}
        </div>

        <div className="vx-search-container">
          <Search className="vx-search-icon" />
          <input
            type="text"
            className="vx-search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar tema, leyes, marinería..."
          />
          {searchQuery && (
            <button className="vx-search-clear" onClick={() => setSearchQuery('')}>✕</button>
          )}
        </div>
      </div>

      {/* ── Malla de Cursos ── */}
      {filteredCourses.length === 0 ? (
        <div style={{
          background: 'rgba(15, 32, 53, 0.6)',
          border: '1px solid rgba(197, 160, 89, 0.2)',
          borderRadius: '14px',
          padding: '3rem',
          textAlign: 'center',
          maxWidth: '480px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <div style={{ fontSize: '2rem' }}>🔍</div>
          <h3 style={{ fontFamily: 'var(--font-tactical)', color: '#fff', margin: 0, fontSize: '1rem' }}>
            No se encontraron cursos
          </h3>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: 0 }}>
            Intenta cambiar los filtros de categoría o buscar con otra palabra clave.
          </p>
          <button
            className="btn-vx-action"
            onClick={() => { setSelectedCategory('Todos'); setSearchQuery(''); }}
          >
            Ver todos los cursos
          </button>
        </div>
      ) : (
        <div className="vx-courses-grid">
          {filteredCourses.map((course) => (
            <div key={course.id} className="vx-course-card">
              {/* Cover */}
              <div>
                <div className="vx-card-cover" onClick={() => onOpenDetailModal(course)}>
                  <img src={course.thumbnail} alt={course.title} />
                  <div className="vx-card-cover-overlay-grad" />
                  <span className="vx-card-cat-badge">{course.category}</span>
                  <span className="vx-card-price-badge">$300 MXN</span>

                  <div className="vx-card-hover-action">
                    <div className="vx-card-hover-btn">
                      <Eye className="w-4 h-4" />
                      <span>Ver Resumen Completo</span>
                    </div>
                  </div>
                </div>

                {/* Body */}
                <div className="vx-card-body">
                  <h3 onClick={() => onOpenDetailModal(course)}>{course.title}</h3>
                  <p>{course.description}</p>
                </div>
              </div>

              {/* Footer */}
              <div className="vx-card-footer">
                <div className="vx-card-specs">
                  <div className="vx-card-spec-item lessons">
                    <BookOpen />
                    <span>{course.resumen?.temas || localCourseResumen(course).temas} Temas · {course.resumen?.lecciones || localCourseResumen(course).lecciones} Lecciones</span>
                  </div>
                  <div className="vx-card-spec-item time">
                    <Clock />
                    <span>{course.resumen?.miniExamenes ?? localCourseResumen(course).miniExamenes} Mini Exámenes · {course.resumen?.examenes ?? localCourseResumen(course).examenes} Exámenes</span>
                  </div>
                </div>

                <div className="vx-card-actions-grid">
                  <button className="btn-card-resumen" onClick={() => onOpenDetailModal(course)}>
                    <Eye className="w-3.5 h-3.5" />
                    <span>Resumen</span>
                  </button>

                  {isSubscribed ? (
                    <button className="btn-card-enter" onClick={() => onSelectCourse(course)}>
                      <Play className="w-3.5 h-3.5" />
                      <span>Ir al Curso</span>
                    </button>
                  ) : (
                    <button className="btn-card-buy" onClick={() => onEnroll(course)}>
                      <CreditCard className="w-3.5 h-3.5" />
                      <span>Adquirir</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Banner Footer Sincronización ── */}
      <div className="vx-footer-banner">
        <div className="vx-footer-banner-left">
          <div className="vx-footer-badge-icon">
            <Award className="w-6 h-6" />
          </div>
          <div>
            <h4>Sincronización de Avance y Contenidos de AscensosApp Vortex</h4>
            <p>
              Todos los datos, cuestionarios y material de estudio están alineados con el banco de información oficial
              de la aplicación móvil de ascensos navales.
            </p>
          </div>
        </div>
      </div>

    </div>
  );
};
